/**
 * Admin Announcements — site-wide banner. Only one active at a time.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api } from "@/App";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const AdminAnnouncementsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [severity, setSeverity] = useState("info");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/announcements")).announcements); }
    catch { toast.error("Could not load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/announcements", { message: msg, severity, active: true });
      toast.success("Banner published — visible to all users");
      setMsg("");
      load();
    } catch { toast.error("Could not publish"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/announcements/${id}`, { withCredentials: true });
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-announcements-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Announcements</h2>
        <p className="text-sm text-muted-foreground">Banner shown on every user&apos;s dashboard. Only one is active at a time — publishing a new one auto-deactivates the previous.</p>
      </div>

      <form onSubmit={create} className="glass border border-border rounded-2xl p-5 mb-6 space-y-3">
        <p className="font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Publish a new announcement</p>
        <textarea data-testid="ann-message" required rows={3} placeholder="e.g. 'New mock exam dropping this Friday at 7pm AEST'" value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <div className="flex items-center gap-2">
          <select data-testid="ann-severity" value={severity} onChange={(e) => setSeverity(e.target.value)} className="px-3 py-2 rounded-lg bg-background border border-border text-sm">
            <option value="info">Info (blue)</option>
            <option value="success">Success (green)</option>
            <option value="warning">Warning (amber)</option>
          </select>
          <button data-testid="ann-publish" type="submit" disabled={saving} className="ml-auto px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving ? "Publishing…" : "Publish to all users"}
          </button>
        </div>
      </form>

      <h3 className="text-sm font-semibold uppercase text-muted-foreground tracking-wider mb-3">History</h3>
      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Message</th>
              <th className="px-4 py-3 font-medium">Severity</th>
              <th className="px-4 py-3 font-medium">Active</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No announcements yet.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-border">
                <td className="px-4 py-3">{a.message}</td>
                <td className="px-4 py-3 text-muted-foreground">{a.severity}</td>
                <td className="px-4 py-3">{a.active ? <span className="text-success">●</span> : <span className="text-muted-foreground">○</span>}</td>
                <td className="px-4 py-3 text-muted-foreground">{new Date(a.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(a.id)} className="p-1.5 rounded hover:bg-destructive/15"><Trash2 className="w-4 h-4 text-destructive" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAnnouncementsPage;
